import { styled, keyframes } from "@pigment-css/react";
import { SidePanelContainer } from "../ui";
import { Flex } from "../../primitives/Flex";
import { Divider } from "../../primitives";

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

// Header
const HeaderTitleSkeleton = styled(SkeletonElement)({
  height: "28px",
  width: "60%",
});

const HeaderSubtitleSkeleton = styled(SkeletonElement)({
  height: "20px",
  width: "30%",
});

// Metrics (4-column grid matching WorkflowMetrics)
const MetricsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  gap: theme.managementView.spacing[16],
}));

const MetricCard = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
  padding: theme.managementView.spacing[16],
  backgroundColor: theme.managementView.colors.neutral[4],
  borderRadius: theme.managementView.radius[10],
}));

const MetricLabelSkeleton = styled(SkeletonElement)({
  height: "14px",
  width: "60px",
});

const MetricValueSkeleton = styled(SkeletonElement)({
  height: "24px",
  width: "50px",
});

// Trigger section
const TriggerHeaderSkeleton = styled(SkeletonElement)({
  height: "20px",
  width: "100px",
});

const TriggerSubtitleSkeleton = styled(SkeletonElement)({
  height: "16px",
  width: "60px",
});

const TriggerRuleSkeleton = styled(SkeletonElement)({
  height: "32px",
  width: "100%",
  borderRadius: "10px",
});

// Config section
const ConfigLabelSkeleton = styled(SkeletonElement)({
  height: "16px",
  width: "80px",
});

const ConfigValueSkeleton = styled(SkeletonElement)({
  height: "16px",
  width: "60px",
});

const ConfigRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: "32px",
  padding: `${theme.managementView.spacing[4]} 0`,
  gap: theme.managementView.spacing[16],
}));

// Activities
const ActivityHeaderSkeleton = styled(SkeletonElement)({
  height: "20px",
  width: "80px",
});

const ActivityDotSkeleton = styled(SkeletonElement)({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  flexShrink: 0,
});

const ActivityBadgeSkeleton = styled(SkeletonElement)({
  height: "21px",
  width: "70px",
  borderRadius: "100px",
});

const ActivityCircleSkeleton = styled(SkeletonElement)({
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  flexShrink: 0,
});

const ActivityTextSkeleton = styled(SkeletonElement)({
  height: "16px",
  width: "140px",
});

const ActivitySubtextSkeleton = styled(SkeletonElement)({
  height: "14px",
  width: "100px",
});

const ActivityRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "13px",
});

const ActivityItemRow = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  paddingLeft: "32px",
  minHeight: "48px",
});

export const WorkflowDetailsSidePanelSkeleton = () => {
  return (
    <SidePanelContainer>
      <Flex direction="column" gap={24}>
        {/* Header */}
        <Flex direction="column" gap={4}>
          <HeaderTitleSkeleton />
          <HeaderSubtitleSkeleton />
        </Flex>

        {/* Metrics */}
        <MetricsGrid>
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCard key={i}>
              <MetricLabelSkeleton />
              <MetricValueSkeleton />
            </MetricCard>
          ))}
        </MetricsGrid>

        <Divider />

        {/* Trigger section */}
        <Flex direction="column" gap={24}>
          <TriggerHeaderSkeleton />
          <TriggerSubtitleSkeleton />
          <Flex direction="column" gap={8}>
            <TriggerRuleSkeleton />
            <TriggerRuleSkeleton />
          </Flex>
        </Flex>

        {/* Config section */}
        <Flex direction="column" gap={8}>
          <ConfigRow>
            <ConfigLabelSkeleton />
            <ConfigValueSkeleton />
          </ConfigRow>
        </Flex>

        <Divider />

        {/* Activities */}
        <Flex direction="column" gap={32}>
          <ActivityHeaderSkeleton />
          <Flex direction="column" gap={12}>
            <ActivityRow>
              <ActivityDotSkeleton />
              <ActivityBadgeSkeleton />
            </ActivityRow>
            {Array.from({ length: 3 }).map((_, i) => (
              <ActivityItemRow key={i}>
                <ActivityCircleSkeleton />
                <Flex direction="column" gap={4}>
                  <ActivityTextSkeleton />
                  <ActivitySubtextSkeleton />
                </Flex>
              </ActivityItemRow>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </SidePanelContainer>
  );
};
